import { cn } from '@/lib/utils'

// ============================================
// BASE SKELETON ELEMENT
// ============================================

interface SkeletonProps {
  className?: string
}

function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-lg bg-muted/50',
        className
      )}
    />
  )
}

// ============================================
// SKELETON CARD
// For room cards, stat cards, etc.
// ============================================

export interface SkeletonCardProps {
  count?: number
  className?: string
}

export function SkeletonCard({ count = 1, className }: SkeletonCardProps) {
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="glass-card rounded-2xl p-4 space-y-3"
        >
          {/* Header row */}
          <div className="flex items-center gap-3">
            <Skeleton className="w-14 h-14 rounded-2xl" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/5 rounded-md" />
              <Skeleton className="h-3 w-2/5 rounded-md bg-muted/30" />
            </div>
            <Skeleton className="w-5 h-5 rounded" />
          </div>
          {/* Content area */}
          <div className="flex items-center gap-4">
            <Skeleton className="h-3 w-16 rounded-md bg-muted/30" />
            <Skeleton className="h-3 w-12 rounded-md bg-muted/30" />
            <Skeleton className="h-3 w-14 rounded-md bg-muted/30" />
          </div>
        </div>
      ))}
    </div>
  )
}

// ============================================
// SKELETON LIST
// For transaction lists, hand history, etc.
// ============================================

export interface SkeletonListProps {
  rows?: number
  className?: string
}

export function SkeletonList({ rows = 5, className }: SkeletonListProps) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 px-4 py-3 glass-card rounded-xl"
          style={{ opacity: 1 - i * 0.12 }}
        >
          {/* Icon placeholder */}
          <Skeleton className="w-10 h-10 rounded-xl flex-shrink-0" />
          {/* Text lines */}
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-2/3 rounded-md" />
            <Skeleton className="h-2.5 w-1/3 rounded-md bg-muted/30" />
          </div>
          {/* Right-side amount */}
          <div className="flex flex-col items-end gap-1.5">
            <Skeleton className="h-3.5 w-16 rounded-md" />
            <Skeleton className="h-2.5 w-10 rounded-md bg-muted/30" />
          </div>
        </div>
      ))}
    </div>
  )
}

// ============================================
// SKELETON PROFILE
// Circle avatar + info lines
// ============================================

export interface SkeletonProfileProps {
  className?: string
}

export function SkeletonProfile({ className }: SkeletonProfileProps) {
  return (
    <div className={cn('flex flex-col items-center gap-4 py-6', className)}>
      {/* Avatar circle */}
      <Skeleton className="w-20 h-20 rounded-full" />
      {/* Name */}
      <Skeleton className="h-5 w-32 rounded-md" />
      {/* Subtitle */}
      <Skeleton className="h-3 w-24 rounded-md bg-muted/30" />
      {/* Stats row */}
      <div className="flex items-center gap-6 mt-2">
        <div className="flex flex-col items-center gap-1.5">
          <Skeleton className="h-6 w-14 rounded-md" />
          <Skeleton className="h-2.5 w-10 rounded-md bg-muted/30" />
        </div>
        <div className="w-px h-8 bg-border/50" />
        <div className="flex flex-col items-center gap-1.5">
          <Skeleton className="h-6 w-14 rounded-md" />
          <Skeleton className="h-2.5 w-10 rounded-md bg-muted/30" />
        </div>
        <div className="w-px h-8 bg-border/50" />
        <div className="flex flex-col items-center gap-1.5">
          <Skeleton className="h-6 w-14 rounded-md" />
          <Skeleton className="h-2.5 w-10 rounded-md bg-muted/30" />
        </div>
      </div>
    </div>
  )
}

// ============================================
// SKELETON TABLE
// Oval poker table shape with seat placeholders
// ============================================

export interface SkeletonTableProps {
  className?: string
}

export function SkeletonTable({ className }: SkeletonTableProps) {
  // 9 seat positions around the table
  const seatPositions = [
    { top: '8%', left: '50%', transform: 'translate(-50%, 0)' },
    { top: '15%', left: '80%', transform: 'translate(-50%, 0)' },
    { top: '40%', left: '92%', transform: 'translate(-50%, 0)' },
    { top: '65%', left: '85%', transform: 'translate(-50%, 0)' },
    { top: '82%', left: '65%', transform: 'translate(-50%, 0)' },
    { top: '82%', left: '35%', transform: 'translate(-50%, 0)' },
    { top: '65%', left: '15%', transform: 'translate(-50%, 0)' },
    { top: '40%', left: '8%', transform: 'translate(-50%, 0)' },
    { top: '15%', left: '20%', transform: 'translate(-50%, 0)' },
  ]

  return (
    <div className={cn('relative w-full aspect-[4/3]', className)}>
      {/* Table oval */}
      <div className="absolute inset-[15%] rounded-[50%] animate-pulse bg-muted/20 border-2 border-muted/30">
        {/* Center pot area */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2">
          <Skeleton className="w-16 h-4 rounded-full bg-muted/40" />
          <Skeleton className="w-10 h-3 rounded-full bg-muted/20" />
        </div>
      </div>

      {/* Seat placeholders */}
      {seatPositions.map((pos, i) => (
        <div
          key={i}
          className="absolute flex flex-col items-center gap-1"
          style={pos}
        >
          <Skeleton className="w-10 h-10 rounded-full" />
          <Skeleton className="w-12 h-2.5 rounded-full bg-muted/30" />
        </div>
      ))}
    </div>
  )
}
